/**
 * Input sanitization for API storage.
 *
 * Strips HTML tags and control characters. Does NOT HTML-entity-encode — encoding
 * belongs at render time (React escapes text nodes). Encoding `/` as &#x2F; etc.
 * caused edit forms to show literal entities like &#x2F; instead of /.
 */

/**
 * Sanitize a user-supplied string for storage:
 *   1. Trim surrounding whitespace.
 *   2. Strip HTML/script tags entirely.
 *   3. Remove null bytes and other control characters (except \t \n \r).
 */
export function sanitizeInput(input: string): string {
  if (typeof input !== "string") return "";

  return input
    .trim()
    .replace(/<[^>]*>/g, "")
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
}

/** Reverse legacy entity encoding when loading records saved with the old sanitizer. */
export function decodeStoredHtmlEntities(value: string): string {
  if (!/&(?:#x?[0-9a-f]+|[a-z]+);/i.test(value)) {
    return value;
  }
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

/** Decode entity-encoded strings on ticket/form records opened for edit. */
export function decodeHtmlEntitiesInRecord<T extends Record<string, unknown>>(
  record: T
): T {
  const out = { ...record };
  for (const key of Object.keys(out)) {
    const v = out[key];
    if (typeof v === "string") {
      (out as Record<string, unknown>)[key] = decodeStoredHtmlEntities(v);
    }
  }
  return out;
}
